import {
  useCallback,
  useState,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { Link } from 'react-router-dom';
import { GlobalButton } from '../components/GlobalButton';
import { GlobalCheckbox } from '../components/GlobalCheckbox';
import GlobalEmptyState from '../components/GlobalEmptyState';
import { GlobalInput } from '../components/GlobalInput';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { useTheme } from '../context/ThemeProvider';
import { useTasks, type LinkedProjectTask } from '../hooks/useTasks';
import type { TaskItem } from '../types';
import styles from './TasksView.module.css';

function GripIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="6" r="1.6" fill="currentColor" />
      <circle cx="15" cy="6" r="1.6" fill="currentColor" />
      <circle cx="9" cy="12" r="1.6" fill="currentColor" />
      <circle cx="15" cy="12" r="1.6" fill="currentColor" />
      <circle cx="9" cy="18" r="1.6" fill="currentColor" />
      <circle cx="15" cy="18" r="1.6" fill="currentColor" />
    </svg>
  );
}

/** Reorders `ids` so `id` moves by `delta` slots, clamped to bounds. */
function moveId(ids: string[], id: string, delta: number): string[] {
  const from = ids.indexOf(id);
  if (from < 0) return ids;
  const to = Math.min(Math.max(from + delta, 0), ids.length - 1);
  if (to === from) return ids;
  const next = ids.slice();
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

export function TasksView() {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const {
    tasks,
    projectTasks,
    loading,
    addTask,
    toggleTask,
    deleteTask,
    reorderTasks,
    toggleProjectTask,
  } = useTasks();

  const [title, setTitle] = useState('');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  const openTasks = tasks.filter((task) => !task.done);
  const doneTasks = tasks.filter((task) => task.done);
  const openProjectTasks = projectTasks.filter((task) => !task.done);
  const doneProjectTasks = projectTasks.filter((task) => task.done);
  const openIds = openTasks.map((task) => task.id);

  function handleAdd(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) return;
    addTask(title);
    setTitle('');
  }

  /** Rebuilds the full list order: open tasks in their new order, done tasks untouched at the end. */
  const applyOpenOrder = useCallback(
    (nextOpenIds: string[]) => {
      reorderTasks([...nextOpenIds, ...doneTasks.map((task) => task.id)]);
    },
    [reorderTasks, doneTasks],
  );

  const handleDragStart = (id: string) => (event: DragEvent<HTMLLIElement>) => {
    setDraggingId(id);
    event.dataTransfer.effectAllowed = 'move';
    try {
      event.dataTransfer.setData('text/plain', id);
    } catch {
      /* some browsers disallow setData outside user gesture */
    }
  };

  const handleDragOver = (id: string) => (event: DragEvent<HTMLLIElement>) => {
    if (!draggingId || draggingId === id) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (id !== dropTargetId) setDropTargetId(id);
  };

  const handleDrop = (targetId: string) => (event: DragEvent<HTMLLIElement>) => {
    event.preventDefault();
    if (draggingId && draggingId !== targetId) {
      const without = openIds.filter((id) => id !== draggingId);
      const targetIndex = without.indexOf(targetId);
      const next = without.slice();
      next.splice(targetIndex < 0 ? without.length : targetIndex, 0, draggingId);
      applyOpenOrder(next);
    }
    setDraggingId(null);
    setDropTargetId(null);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDropTargetId(null);
  };

  const handleGripKeyDown = (id: string) => (event: KeyboardEvent<HTMLButtonElement>) => {
    let delta = 0;
    if (event.key === 'ArrowUp') delta = -1;
    else if (event.key === 'ArrowDown') delta = 1;
    else return;
    event.preventDefault();
    const next = moveId(openIds, id, delta);
    if (next !== openIds) applyOpenOrder(next);
  };

  const trashIcon = (
    <img
      src={isDarkMode ? '/assets/icons/trash.svg' : '/assets/icons/trash.black.svg'}
      alt=""
      width={13}
      height={13}
    />
  );

  const renderProjectTask = (task: LinkedProjectTask) => {
    return (
      <li key={task.id} className={`${styles.taskRow} ${task.done ? styles.taskRowDone : ''}`}>
        <span className={styles.gripSpacer} aria-hidden />
        <GlobalCheckbox
          className={styles.taskCheckbox}
          checked={task.done}
          onChange={() => toggleProjectTask(task.id, task.propertyId)}
          label={task.title}
        />
        <div className={styles.projectBadge}>
          <Link to={`/real-estate/${task.propertyId}?tab=project`} className={styles.projectLink}>
            {task.propertyName}
            {task.phaseName ? ` · ${task.phaseName}` : ''}
          </Link>
        </div>
      </li>
    );
  };

  const renderTask = (task: TaskItem, draggable: boolean) => {
    const isDragging = draggingId === task.id;
    const isDropTarget = dropTargetId === task.id && draggingId !== task.id;
    const rowClass = [
      styles.taskRow,
      task.done ? styles.taskRowDone : '',
      isDragging ? styles.taskRowDragging : '',
      isDropTarget ? styles.taskRowDropTarget : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <li
        key={task.id}
        className={rowClass}
        draggable={draggable}
        onDragStart={draggable ? handleDragStart(task.id) : undefined}
        onDragOver={draggable ? handleDragOver(task.id) : undefined}
        onDrop={draggable ? handleDrop(task.id) : undefined}
        onDragEnd={draggable ? handleDragEnd : undefined}
      >
        {draggable ? (
          <button
            type="button"
            className={styles.grip}
            aria-label={`Reorder "${task.title}". Use arrow keys to move.`}
            title="Drag to reorder"
            onKeyDown={handleGripKeyDown(task.id)}
            onClick={(e) => e.preventDefault()}
          >
            <GripIcon />
          </button>
        ) : (
          <span className={styles.gripSpacer} aria-hidden />
        )}
        <GlobalCheckbox
          className={styles.taskCheckbox}
          checked={task.done}
          onChange={() => toggleTask(task.id)}
          label={task.title}
        />
        <button
          type="button"
          className={styles.deleteButton}
          aria-label={`Delete "${task.title}"`}
          title="Delete task"
          onClick={() => deleteTask(task.id)}
        >
          {trashIcon}
        </button>
      </li>
    );
  };

  return (
    <ViewPage title="Tasks">
      <ViewSectionHeader
        eyebrow="Tasks"
        title="To-do list"
        subtitle="Capture quick to-dos, check them off, and drag to reorder."
      />

      <section className={styles.root}>
        <form className={styles.addForm} onSubmit={handleAdd}>
          <GlobalInput
            label="Add a task"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={500}
          />
          <GlobalButton type="submit" variant="secondary" autoWidth disabled={!title.trim()}>
            Add
          </GlobalButton>
        </form>

        {loading ? (
          <p className="muted">Loading tasks…</p>
        ) : tasks.length === 0 && projectTasks.length === 0 ? (
          <GlobalEmptyState />
        ) : (
          <>
            {openTasks.length > 0 || openProjectTasks.length > 0 ? (
              <div className={styles.listCard}>
                <ul className={styles.list}>
                  {openTasks.map((task) => renderTask(task, true))}
                  {openProjectTasks.map((task) => renderProjectTask(task))}
                </ul>
              </div>
            ) : (
              <p className="muted">All done — nothing left on the list.</p>
            )}

            {doneTasks.length > 0 || doneProjectTasks.length > 0 ? (
              <div className={styles.doneSection}>
                <p className={styles.doneHeading}>
                  Completed ({doneTasks.length + doneProjectTasks.length})
                </p>
                <div className={styles.listCard}>
                  <ul className={styles.list}>
                    {doneTasks.map((task) => renderTask(task, false))}
                    {doneProjectTasks.map((task) => renderProjectTask(task))}
                  </ul>
                </div>
              </div>
            ) : null}
          </>
        )}
      </section>
    </ViewPage>
  );
}
